import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { authFetch } from '../utils/authFetch';
import PhysicalWeight from './PhysicalWeight';

export function normalizeUnit(value) {
  const unit = (value || '').trim().toUpperCase();
  if (['M', 'ML', 'M.L.', 'METRU', 'METRI'].includes(unit)) return 'M';
  if (['M2', 'MP', 'M²'].includes(unit)) return 'M2';
  if (['KG', 'KILOGRAM'].includes(unit)) return 'KG';
  return unit;
}

export function allowedReceiptUnits(units, part, conversion) {
  const stockId = part?.um?.id ?? part?.um_id;
  const stock = normalizeUnit(part?.um?.cod || units[stockId]);
  const factor = Number(conversion?.kg_per_unit);
  const physical = conversion?.kind === 'linear' ? 'M' : conversion?.kind === 'sheet' ? 'M2' : null;
  const convertible = Number.isFinite(factor) && factor > 0 &&
    physical && [physical, 'KG'].includes(stock);
  return Object.entries(units).filter(([id, label]) =>
    String(stockId) === id || (stock && normalizeUnit(label) === stock) ||
    (convertible && [physical, 'KG'].includes(normalizeUnit(label))));
}

export default function ReceiptUnitSelect({ partId, value, units, disabled, onChange, name, quantity }) {
  const [loaded, setLoaded] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let active = true;
    setLoaded(null);
    setError('');
    if (partId) Promise.all([
      authFetch(`/bxpart/${partId}`),
      authFetch(`/productie-simaus/materiale/conversii/${partId}`),
    ]).then(([part, conversion]) => {
      if (active) setLoaded({ partId, part, conversion });
    }).catch(() => { if (active) setError('Nu s-au putut încărca unitățile articolului. Reîncărcați pagina.'); });
    return () => { active = false; };
  }, [partId]);
  const ready = loaded?.partId === partId && !!partId;
  const options = ready ? allowedReceiptUnits(units, loaded.part, loaded.conversion) : [];
  const conversion = ready ? loaded.conversion : null;
  const unit = normalizeUnit(units[value]);
  const physicalUnit = conversion?.kind === 'sheet' ? 'M2' : 'M';
  const factor = Number(conversion?.kg_per_unit);
  const quantityNumber = Number(String(quantity ?? '').replace(',', '.'));
  const physical = unit === 'KG' ? quantityNumber / factor : quantityNumber;
  const size = Number(conversion?.default_length_m) * (conversion?.kind === 'sheet' ? Number(conversion?.default_width_m) : 1);
  const preview = factor > 0 && size > 0 && quantityNumber > 0 && ['KG', physicalUnit].includes(unit);
  // Preserve historical invoice units; never rewrite an existing line while loading.
  if (value != null && !options.some(([id]) => id === String(value))) {
    options.unshift([String(value), units[value] || `UM #${value}`]);
  }
  return <>
    <Form.Select size="sm" name={name} aria-label="UM factură" value={value ?? ''}
      disabled={disabled || !ready || options.length < 2}
      title="Cantitatea și prețul sunt în UM facturii. Conversia în UM articolului se face la NIR."
      onChange={e => onChange(Number(e.target.value))}>
      {value == null && <option value="">—</option>}
      {options.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
    </Form.Select>
    {preview && <div style={{ minWidth: 145 }}><small>{physical.toLocaleString('ro-RO', { maximumFractionDigits: 6 })} {physicalUnit === 'M' ? 'ML' : 'm²'} · {(physical * factor).toLocaleString('ro-RO', { maximumFractionDigits: 6 })} KG</small>
      <div><small>La dimensiunea uzuală: {(physical / size).toLocaleString('ro-RO', { maximumFractionDigits: 6 })} buc. × {conversion.default_length_m} m{conversion.kind === 'sheet' ? ` × ${conversion.default_width_m} m` : ''}</small></div>
      <PhysicalWeight pieces={physical / size} length={conversion.default_length_m} width={conversion.default_width_m} kind={conversion.kind} factor={factor}/>
      <small className="text-muted">Dimensiunile efective se confirmă în NIR.</small>
    </div>}
    {error && <small role="alert" className="text-danger">{error}</small>}
  </>;
}
